import React, { useMemo } from 'react';
import Dialog from 'components/shared/Dialog';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import { findLatestLastUpdatedValue, formatTimestampToLocal } from 'common/utils/helpers';
import { PerfectScrollbar } from 'components/shared/ReactPerfectScrollbar';
import get from 'lodash/get';

import styles from './GA2SwVersionModal.module.css';

const SW_RELEASES_HEADERS = ['sys-sw-rel', 'cntlr', 'du-rfrx', 'du-rftx', 'su-rfrx', 'su-rftx'];
const SW_VERSIONS_HEADERS = [
  'cntlr',
  'cntlr-active',
  'cntlr-active-on-next-reset',
  'rfrx',
  'rfrx-active',
  'rfrx-active-on-next-reset',
  'rftx',
  'rftx-active',
  'rftx-active-on-next-reset'
];

function GA2SwVersionModal({ device, onClose }) {
  const releaseLastUpdated = formatTimestampToLocal(
    get(device, 'properties.reported.$metadata.DU.swdl-releases.$lastUpdated', null)
  );
  const latestVersionLastUpdated = useMemo(() => findLatestLastUpdatedValue(device, 'swdl-versions'), [device]);
  const getSingleReleaseCol = () => {
    const DuDeviceId = get(device, 'properties.reported.DU.inventory-info.serial-id', null);
    const activeRelease = get(device, 'properties.reported.DU.swdl-releases.active-release', {});
    const inactiveRelease = get(device, 'properties.reported.DU.swdl-releases.inactive-release', {});
    const activeReleaseOnNextReset = get(
      device,
      'properties.reported.DU.swdl-releases.active-release-on-next-reset',
      {}
    );

    return (
      <div className={styles.dataContent}>
        <div className={styles.deviceIdContainer}>
          <SvgAF img={IconLoader.icon_donor} />
          <span className={styles.deviceId}>{DuDeviceId ?? 'Unknown'}</span>
        </div>
        <div className={styles.releaseVersionContainer}>
          <div className={styles.singleColContainer}>
            <h5 className={styles.activeInactiveTitle}>ACTIVE</h5>
            {SW_RELEASES_HEADERS.map((header) => (
              <div className={styles.dataGrid}  title={getDataTooltip(getDataDisplayString(activeRelease?.[header]))}>{activeRelease?.[header] ?? 'unknown'}</div>
            ))}
            <h5 className={styles.resetTitle}>ACTIVE ON NEXT RESET</h5>
            <div className={styles.dataGrid}>{activeReleaseOnNextReset}</div>
          </div>
          <div className={styles.singleColContainer}>
            <h5 className={styles.activeInactiveTitle}>INACTIVE</h5>
            {SW_RELEASES_HEADERS.map((header) => (
              <div className={styles.dataGrid} title={getDataTooltip(getDataDisplayString(inactiveRelease?.[header]))}>{inactiveRelease?.[header] ?? 'unknown'}</div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  /**
   * returns a list of unit info including id, version, Du unit or not
   */
  const getSwldVersions = () => {
    const DuVersions = get(device, 'properties.reported.DU.swdl-versions', null);
    const DuDeviceId = get(device, 'properties.reported.DU.inventory-info.serial-id', null);
    let versions = [{ deviceId: DuDeviceId, versions: DuVersions, Du: true }];

    Object.keys(get(device, 'properties.reported', {})).forEach((unit) => {
      if (unit === 'DU') return;
      const SuVersion = get(device, `properties.reported.${unit}.swdl-versions`, null);
      const SuDeviceId = get(device, `properties.reported.${unit}.provision-info.expected-serial-id`, null);
      if (!SuVersion && !SuDeviceId) return;
      versions.push({ deviceId: SuDeviceId, versions: SuVersion, Du: false });
    });

    return versions;
  };

  const getDataDisplayString = (rawData) => {
    if (rawData === undefined) return 'unknown';

    return rawData.toString();
  };

  const getDataTooltip = (data) => {
    if (data && data.length < 11) return '';
    return data;
  };

  const getSingleVersionCol = ({ deviceId, versions, Du }, index) => {
    return (
      <div className={styles.dataContent} key={index}>
        <div className={styles.deviceIdContainer}>
          <SvgAF img={Du ? IconLoader.icon_donor : IconLoader.icon_service} />
          <span className={styles.deviceId}>{deviceId ?? 'Unknown'}</span>
        </div>
        <div className={styles.releaseVersionContainer}>
          <div className={styles.singleColContainer}>
            <h5 className={styles.activeInactiveTitle}>PARTITION-A</h5>
            {SW_VERSIONS_HEADERS.map((header, index) => (
              <div
                className={styles.dataGrid}
                title={getDataTooltip(getDataDisplayString(versions?.['partition-A']?.[header]))}
                key={index}
              >
                {getDataDisplayString(versions?.['partition-A']?.[header])}
              </div>
            ))}
          </div>
          <div className={styles.singleColContainer}>
            <h5 className={styles.activeInactiveTitle}>PARTITION-B</h5>
            {SW_VERSIONS_HEADERS.map((header, index) => (
              <div
                className={styles.dataGrid}
                title={getDataTooltip(getDataDisplayString(versions?.['partition-B']?.[header]))}
                key={index}
              >
                {getDataDisplayString(versions?.['partition-B']?.[header])}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <Dialog.Container maxWidth={1120} maxHeight={900}>
        <Dialog.Title>
          <h3 className={styles.title}>SW Release and Versions</h3>
        </Dialog.Title>
        <Dialog.Content>
          <div className={styles.content}>
            <div className={styles.topContent}>
              <div className={styles.colHeader}>
                <div className={styles.headerTitle}>Releases</div>
                <div className={styles.headerContent}>
                  {SW_RELEASES_HEADERS.map((header, index) => (
                    <div className={styles.headerGrid} key={index}>
                      {header}
                    </div>
                  ))}
                </div>
              </div>
              {getSingleReleaseCol()}
              <div className={styles.lastUpdated}>
                <div className={styles.lastUpdatedTitle}>Last Updated: {releaseLastUpdated}</div>
              </div>
            </div>
            <div className={styles.botContent}>
              <div className={styles.colHeader}>
                <div className={styles.headerTitle}>Versions</div>
                <div className={styles.headerContent}>
                  {SW_VERSIONS_HEADERS.map((header, index) => (
                    <div className={styles.headerGrid} key={index}>
                      {header}
                    </div>
                  ))}
                </div>
              </div>
              <PerfectScrollbar>
                <div className={styles.versionContainer}>
                  {getSwldVersions().map((unit, index) => getSingleVersionCol(unit, index))}
                </div>
              </PerfectScrollbar>
              <div className={styles.lastUpdated}>
                <div className={styles.lastUpdatedTitle}>Last Updated: {latestVersionLastUpdated}</div>
              </div>
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Close />
      </Dialog.Container>
    </Dialog>
  );
}

export default GA2SwVersionModal;
